import React, { useMemo } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp, Save, Loader2 } from 'lucide-react';
import {
  DEFAULT_PRINT_SETTINGS,
  SHEET_SIZES,
  mergePrintSettings,
} from '../../utils/idCardPrintSettings';
import { computeSheetGrid } from '../../utils/idCardSheetLayout';
import { ID_CARD_LANDSCAPE, ID_CARD_PORTRAIT } from '../../utils/idCardTheme';

const SHEET_OPTIONS = Object.entries(SHEET_SIZES).map(([value, { label }]) => ({ value, label }));

export default function IdCardPrintSettingsPanel({
  settings,
  onChange,
  onSave,
  saving = false,
  defaultOpen = true,
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const merged = useMemo(() => mergePrintSettings(settings), [settings]);
  const grid = useMemo(() => computeSheetGrid(merged), [merged]);

  const set = (key, value) => onChange({ ...merged, [key]: value });

  const handleFormatChange = (fmt) => {
    const preset = fmt === 'landscape' ? ID_CARD_LANDSCAPE : ID_CARD_PORTRAIT;
    onChange({
      ...merged,
      cardFormat: fmt,
      cardWidthIn: preset.widthIn,
      cardHeightIn: preset.heightIn,
    });
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div>
          <p className="font-medium">Print sheet settings</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Layout used for bulk preview, print, and PDF export
          </p>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label>Sheet size</Label>
              <Select value={merged.sheetSize} onValueChange={(v) => set('sheetSize', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SHEET_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Sheet orientation</Label>
              <Select value={merged.sheetOrientation} onValueChange={(v) => set('sheetOrientation', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">Portrait</SelectItem>
                  <SelectItem value="landscape">Landscape</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Card orientation</Label>
              <Select value={merged.cardFormat} onValueChange={handleFormatChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">Portrait (2″ × 3″)</SelectItem>
                  <SelectItem value="landscape">Landscape (3″ × 2″)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Card width (in)</Label>
              <Input
                type="number"
                min={1}
                max={4}
                step={0.1}
                value={merged.cardWidthIn}
                onChange={(e) => set('cardWidthIn', Number(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label>Card height (in)</Label>
              <Input
                type="number"
                min={1.5}
                max={6}
                step={0.1}
                value={merged.cardHeightIn}
                onChange={(e) => set('cardHeightIn', Number(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label>Margin (mm)</Label>
              <Input
                type="number"
                min={0}
                max={40}
                step={0.5}
                value={merged.marginMm}
                onChange={(e) => set('marginMm', Number(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label>Horizontal gap (mm)</Label>
              <Input
                type="number"
                min={0}
                max={30}
                step={0.5}
                value={merged.gapHorizontalMm}
                onChange={(e) => set('gapHorizontalMm', Number(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label>Vertical gap (mm)</Label>
              <Input
                type="number"
                min={0}
                max={30}
                step={0.5}
                value={merged.gapVerticalMm}
                onChange={(e) => set('gapVerticalMm', Number(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label>Cards per row</Label>
              <Input
                type="number"
                min={1}
                max={20}
                disabled={merged.autoFit}
                value={merged.autoFit ? grid.cols : (merged.cardsPerRow ?? grid.cols)}
                onChange={(e) => set('cardsPerRow', Number(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label>Cards per column</Label>
              <Input
                type="number"
                min={1}
                max={20}
                disabled={merged.autoFit}
                value={merged.autoFit ? grid.rows : (merged.cardsPerColumn ?? grid.rows)}
                onChange={(e) => set('cardsPerColumn', Number(e.target.value))}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-3">
              <Switch id="auto-fit" checked={merged.autoFit} onCheckedChange={(v) => set('autoFit', v)} />
              <Label htmlFor="auto-fit">Auto-fit max cards per page</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="include-back" checked={merged.includeBack} onCheckedChange={(v) => set('includeBack', v)} />
              <Label htmlFor="include-back">Include card back (side-by-side)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="crop-marks" checked={merged.showCropMarks} onCheckedChange={(v) => set('showCropMarks', v)} />
              <Label htmlFor="crop-marks">Show crop marks</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="page-border" checked={merged.showPageBorder} onCheckedChange={(v) => set('showPageBorder', v)} />
              <Label htmlFor="page-border">Show page border</Label>
            </div>
          </div>

          <div className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground space-y-1">
            <p>
              Grid: {grid.cols} × {grid.rows} card faces · {grid.studentsPerPage} student{grid.studentsPerPage !== 1 ? 's' : ''} per page
              · Page {grid.pageWidthMm.toFixed(1)} × {grid.pageHeightMm.toFixed(1)} mm
            </p>
            {grid.warnings.map((w) => (
              <p key={w} className="text-amber-600">{w}</p>
            ))}
          </div>

          <div className="flex justify-end">
            <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => onSave?.(merged)}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save as school default
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export { DEFAULT_PRINT_SETTINGS };
